import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { VoiceChatProvider, useVoiceChatContext } from '../../contexts/VoiceChatContext';
import apiService from '../../services/apiService';
import ChatHeader from '../assessment/ChatHeader';
import MessageList from '../assessment/MessageList';
import ChatInput from '../assessment/ChatInput';
import '../../styles/chat.css';

// Inner component — requires VoiceChatContext to be present above it
const ChatViewInner = ({ conversationId, conversation, messages, setMessages, onTitleUpdate }) => {
  const { user } = useAuth();
  const voiceChat = useVoiceChatContext();
  const [isSending, setIsSending] = useState(false); // eslint-disable-line no-unused-vars

  useEffect(() => {
    voiceChat.connect();
  }, [conversationId]); 

  const handleSendMessage = async (messageText, { isVoice = false } = {}) => {
    const userMessage = {
      id: Date.now().toString(),
      content: messageText,
      role: 'user',
      sender: 'user',
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsSending(true);

    try {
      const response = await apiService.post(
        `/api/v1/conversations/${conversationId}/messages`,
        { message: messageText },
        user
      );

      if (response?.success && response?.data) {
        const aiResponseText =
          response.data?.aiResponse ||
          response.aiResponse ||
          response.response ||
          response.message ||
          response.data?.response ||
          response.data?.message;

        if (!aiResponseText) throw new Error('No AI response received');

        const aiMessage = {
          id: (Date.now() + 1).toString(),
          content: aiResponseText,
          role: 'ai',
          sender: 'ai',
          timestamp: new Date().toISOString(),
        };

        setMessages(prev => [...prev, aiMessage]);

        if (isVoice && voiceChat?.generateSpeech) {
          try {
            await voiceChat.generateSpeech(aiResponseText);
          } catch (err) {
            console.error('Error generating speech:', err);
          }
        }
      }
    } catch (err) {
      console.error('ChatView send error:', err);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        content: 'Sorry, I had trouble processing your message. Please try again.',
        role: 'ai',
        sender: 'ai',
        timestamp: new Date().toISOString(),
        isError: true,
      }]);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="chat-view">
      <ChatHeader conversation={conversation} onTitleUpdate={onTitleUpdate} />
      <MessageList messages={messages} />
      <ChatInput onSendMessage={handleSendMessage} />
    </div>
  );
};

ChatViewInner.propTypes = {
  conversationId: PropTypes.string.isRequired,
  conversation: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    methodology: PropTypes.string,
  }).isRequired,
  messages: PropTypes.array.isRequired,
  setMessages: PropTypes.func.isRequired,
  onTitleUpdate: PropTypes.func.isRequired,
};

// Outer wrapper provides VoiceChatContext (required by ChatInput)
const ChatView = (props) => (
  <VoiceChatProvider conversationId={props.conversationId}>
    <ChatViewInner {...props} />
  </VoiceChatProvider>
);

ChatView.propTypes = {
  conversationId: PropTypes.string.isRequired,
  conversation: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    methodology: PropTypes.string,
  }).isRequired,
  messages: PropTypes.array.isRequired,
  setMessages: PropTypes.func.isRequired,
  onTitleUpdate: PropTypes.func.isRequired,
};

export default ChatView;
